import { useState, useEffect } from "react";
import axios from "axios";
import party from "party-js";
import Layout from "../Layout";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import ApartmentIcon from "@mui/icons-material/Apartment";
import StoreMallDirectoryIcon from "@mui/icons-material/StoreMallDirectory";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PlaceIcon from "@mui/icons-material/Place";

party.settings.debug = false;

const confettiSettings = {
    shapes: [
        "square",
        "rectangle",
        "roundedSquare",
        "circle",
        "roundedRectangle",
        "star",
    ],
    // The amount of confetti particles that should be emitted.
    count: party.variation.range(80, 200),
    // The amount of spread that is applied to the emission angle. Note that the default angle points upwards.
    spread: 50,
    // The initial speed that the confetti particles are emitted with.
    speed: party.variation.range(150, 600),
    // The initial size that the confetti particles are emitted with.
    size: party.variation.skew(1, 0.8),
    // The initial rotation that the confetti particles are emitted with.
    rotation: () => party.random.randomUnitVector().scale(180),
    // The initial color that particles are emitted with.
    color: () =>
        party.random.pick([
            party.Color.fromHex("#238446"),
            party.Color.fromHex("#1D7DB2"),
            party.Color.fromHex("#FFC800"),
            party.Color.fromHex("#FFFFFF"),
        ]),
};

const PROGRESS_STEPS = ["Interview", "Onboarding Form", "Contract Signed", "First Day"];

export default function Completed() {
    const [siteData, setSiteData] = useState();
    const [staffName, setStaffName] = useState("");
    const [sitePhone, setSitePhone] = useState("");

    useEffect(() => {
        let site = null;
        let staff = "";
        try {
            const rawStaff = sessionStorage.getItem("staff");
            staff = rawStaff ? JSON.parse(rawStaff ?? "null") : "";
        } catch {
            staff = "";
        }
        try {
            const rawSite = sessionStorage.getItem("site");
            site = rawSite ? JSON.parse(rawSite ?? "null") : null;
        } catch {
            site = null;
        }
        setStaffName(staff);
        party.confetti(document.getElementById("confetti"), confettiSettings);

        const baseUrl = process.env.REACT_APP_HR_API_URL;
        const token = sessionStorage.getItem("onboardingToken");
        const headers = token ? { "x-onboarding-token": token } : {};
        if (!site) return;
        axios
            .get(
                `${baseUrl}/onBoard/new-start/site/${site}`,
                { headers }
            )
            .then((res) => {
                setSiteData(res.data);
                if (res.data?.Address?.phoneNumber) {
                    setSitePhone(res.data.Address.phoneNumber);
                }
            })
            .catch(() => {});
    }, []);

    return (
        <Layout>
            <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 }, textAlign: "center" }}>
                <Typography
                    id="confetti"
                    variant="h3"
                    color="primary.dark"
                    sx={{
                        fontWeight: 700,
                        textTransform: "uppercase",
                        animation: "wiggle 1.3s infinite",
                        "@keyframes wiggle": {
                            "0%": { transform: "translateY(0) scale(1)" },
                            "50%": { transform: "translateY(-5px) scale(1.15)" },
                            "100%": { transform: "translateY(0) scale(1)" },
                        },
                    }}
                >
                    Congratulations!
                </Typography>
                <Typography variant="h6" sx={{ mt: 1, mb: 4 }}>
                    <Box component="span" color="error.main" sx={{ fontWeight: 700 }}>
                        {staffName},
                    </Box>{" "}
                    welcome to the team
                </Typography>

                <Stepper activeStep={3} alternativeLabel sx={{ mb: 4 }}>
                    {PROGRESS_STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Typography variant="body1" sx={{ mb: 3 }}>
                    While you wait for your first day, if you need to contact us
                    please take note of these numbers:
                </Typography>

                <Stack spacing={2}>
                    <Card variant="outlined">
                        <CardContent sx={{ textAlign: "left" }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <ApartmentIcon color="error" />
                                <Typography variant="h6">HR</Typography>
                            </Stack>
                            <List dense disablePadding>
                                <ListItem disableGutters>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <PhoneIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box component="a" href="tel:01415301655" color="inherit">
                                                0141 530 1655
                                            </Box>
                                        }
                                    />
                                </ListItem>
                                <ListItem disableGutters>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <EmailIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box
                                                component="a"
                                                href="mailto:team@jedihrsolutions.co.uk"
                                                color="inherit"
                                                sx={{ wordBreak: "break-all" }}
                                            >
                                                team@jedihrsolutions.co.uk
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>

                    {siteData ? (
                        <Card variant="outlined">
                            <CardContent sx={{ textAlign: "left" }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <StoreMallDirectoryIcon color="error" />
                                    <Typography variant="h6">{siteData.name}</Typography>
                                </Stack>
                                <List dense disablePadding>
                                    <ListItem disableGutters>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <PlaceIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary={siteData.Address.addressLine1} />
                                    </ListItem>
                                    <ListItem disableGutters>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <PhoneIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box
                                                    component="a"
                                                    href={sitePhone ? `tel:${sitePhone}` : undefined}
                                                    id="tel"
                                                    color="inherit"
                                                >
                                                    {siteData.Address.phoneNumber}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    ) : (
                        <></>
                    )}
                </Stack>
            </Container>
        </Layout>
    );
}
